import React, { useState, useCallback, useEffect } from 'react';
import { T, FONTS, SHADOW } from '../../constants/theme.js';
import {
  fetchRevenueSummary, fetchOccupancyReport, fetchArrivalManifest,
  fetchDepartureManifest, fetchChannelReport, fetchFolioReport, fetchHKReport,
} from '../../services/api.js';
import { useOfflineInvalidation } from '../../components/ui.jsx';
import { usePendingSync } from '../../hooks/usePendingSync.js';

/* ── date helpers ──────────────────────────────────────────────────────────── */
const today      = new Date().toISOString().slice(0, 10);
const monthStart = new Date(new Date().getFullYear(), new Date().getMonth(), 1).toISOString().slice(0, 10);
const daysAgo    = n => new Date(Date.now() - n * 86400000).toISOString().slice(0, 10);

const fmtD = d => d ? new Date(d + 'T00:00:00').toLocaleDateString('en-GB',
  { day:'numeric', month:'short', year:'numeric' }) : '—';
const fmtNum = v => Number(v||0).toLocaleString();
const fmtMoney = (v, cur) =>
  new Intl.NumberFormat('en-US', { style:'currency', currency:cur||'RWF', minimumFractionDigits:0 }).format(Number(v||0));
const cap = s => s ? s.replace(/_/g,' ').replace(/\b\w/g, c=>c.toUpperCase()) : '—';
const pct = (a, b) => b > 0 ? ((a / b) * 100).toFixed(1) + '%' : '—';

/* ── CSV export helper ─────────────────────────────────────────────────────── */
function exportCSV(headers, rows, filename) {
  const escape = v => {
    const s = String(v ?? '');
    return s.includes(',') || s.includes('"') || s.includes('\n') ? `"${s.replace(/"/g,'""')}"` : s;
  };
  const csv = [headers, ...rows].map(r => r.map(escape).join(',')).join('\r\n');
  const blob = new Blob(['\uFEFF' + csv], { type: 'text/csv;charset=utf-8;' });
  const url  = URL.createObjectURL(blob);
  const a    = document.createElement('a'); a.href = url; a.download = filename; a.click();
  URL.revokeObjectURL(url);
}

/* ── micro UI pieces ───────────────────────────────────────────────────────── */
function KpiCard({ label, value, sub, color, icon, highlight }) {
  return (
    <div style={{ background:highlight?`linear-gradient(135deg,${T.navy},${T.navyLight})`:T.white,
      borderRadius:13, padding:'16px 18px', border:`1px solid ${highlight?'transparent':T.border}`,
      boxShadow:SHADOW.sm }}>
      <div style={{ display:'flex',justifyContent:'space-between',alignItems:'flex-start',marginBottom:8 }}>
        <p style={{ fontSize:9, fontWeight:700, color:highlight?`rgba(255,255,255,.55)`:T.muted,
          letterSpacing:.9, textTransform:'uppercase' }}>{label}</p>
        {icon && <span style={{ fontSize:18 }}>{icon}</span>}
      </div>
      <p style={{ fontFamily:FONTS.display, fontSize:22, fontWeight:700,
        color:highlight?T.gold:(color||T.navy), lineHeight:1 }}>{value}</p>
      {sub && <p style={{ fontSize:10, color:highlight?`rgba(255,255,255,.45)`:T.muted, marginTop:4 }}>{sub}</p>}
    </div>
  );
}

function Badge({ label, color }) {
  return (
    <span style={{ display:'inline-block', padding:'2px 8px', borderRadius:99,
      fontSize:10, fontWeight:700, background:`${color}18`, color, border:`1px solid ${color}25` }}>
      {label}
    </span>
  );
}

function SectionTitle({ children, action }) {
  return (
    <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:14 }}>
      <p style={{ fontSize:13, fontWeight:700, color:T.navy }}>{children}</p>
      {action}
    </div>
  );
}

function Divider() { return <div style={{ borderTop:`1px solid ${T.border}`, margin:'16px 0' }} />; }

function ExportBtn({ onClick }) {
  return (
    <button onClick={onClick} style={{ display:'flex', alignItems:'center', gap:5,
      padding:'6px 13px', borderRadius:8, border:`1px solid ${T.border}`,
      background:T.surface, fontSize:11, fontWeight:700, color:T.slate,
      cursor:'pointer', fontFamily:FONTS.body }}>
      📥 Export Excel
    </button>
  );
}

/* ── SVG bar chart (vertical) ─────────────────────────────────────────────── */
function BarChart({ data = [], valueKey = 'value', labelKey = 'label', color = T.teal,
  color2, value2Key, height = 160, currency, maxBars = 31 }) {
  if (!data.length) return <p style={{ textAlign:'center', padding:24, color:T.muted, fontSize:12 }}>No data</p>;
  const slice  = data.slice(0, maxBars);
  const vals   = slice.map(d => parseFloat(d[valueKey]||0));
  const vals2  = color2 && value2Key ? slice.map(d => parseFloat(d[value2Key]||0)) : [];
  const maxVal = Math.max(...vals, ...vals2, 1);
  const W = 100 / slice.length;

  return (
    <div style={{ overflowX:'auto' }}>
      <div style={{ minWidth: slice.length * 28, height, position:'relative', padding:'0 8px' }}>
        {slice.map((d, i) => {
          const v1  = vals[i];
          const v2  = vals2[i] || 0;
          const h1  = ((v1 / maxVal) * (height - 30)).toFixed(1);
          const h2  = ((v2 / maxVal) * (height - 30)).toFixed(1);
          const lbl = d[labelKey]?.slice(5) || ''; // strip year from dates
          return (
            <div key={i} title={
              color2
                ? `${d[labelKey] || lbl}\nRoom: ${currency ? fmtMoney(v1, currency) : fmtNum(v1)}\nOther: ${currency ? fmtMoney(v2, currency) : fmtNum(v2)}`
                : `${d[labelKey] || lbl}: ${currency ? fmtMoney(v1, currency) : fmtNum(v1)}`
            }
              style={{ position:'absolute', bottom:0, left:`${i*W}%`, width:`${W}%`,
                paddingBottom:22, display:'flex', flexDirection:'column', alignItems:'center', gap:1 }}>
              {color2 && (
                <div style={{ width:'38%', height:`${h2}px`, background:`${color2}90`, borderRadius:'3px 3px 0 0' }} />
              )}
              <div style={{ width: color2 ? '38%' : '60%', height:`${h1}px`,
                background:color, borderRadius:'3px 3px 0 0', minHeight:2 }} />
              <p style={{ position:'absolute', bottom:4, fontSize:8, color:T.muted,
                textAlign:'center', maxWidth:'100%', overflow:'hidden', whiteSpace:'nowrap' }}>{lbl}</p>
            </div>
          );
        })}
      </div>
      {color2 && (
        <div style={{ display:'flex', gap:16, justifyContent:'center', marginTop:6 }}>
          <div style={{ display:'flex', alignItems:'center', gap:5 }}>
            <div style={{ width:10, height:10, borderRadius:2, background:color }} />
            <span style={{ fontSize:10, color:T.muted }}>Room Rev.</span>
          </div>
          <div style={{ display:'flex', alignItems:'center', gap:5 }}>
            <div style={{ width:10, height:10, borderRadius:2, background:`${color2}90` }} />
            <span style={{ fontSize:10, color:T.muted }}>Other Rev.</span>
          </div>
        </div>
      )}
    </div>
  );
}

/* ── SVG donut chart ──────────────────────────────────────────────────────── */
const DONUT_COLORS = [T.teal, T.gold, T.navy, '#8B5CF6', '#EC4899', '#10B981', '#F59E0B', '#3B82F6'];
function DonutChart({ data = [], valueKey = 'value', labelKey = 'label', size = 140 }) {
  if (!data.length) return null;
  const total  = data.reduce((s, d) => s + parseFloat(d[valueKey]||0), 0);
  if (total === 0) return null;
  const r = 44, cx = 55, cy = 55, circ = 2 * Math.PI * r;
  let offset = 0;
  const slices = data.map((d, i) => {
    const val   = parseFloat(d[valueKey]||0);
    const share = val / total;
    const dash  = share * circ;
    const gap   = circ - dash;
    const slice = { offset, dash, gap, color: DONUT_COLORS[i % DONUT_COLORS.length] };
    offset += dash;
    return slice;
  });
  return (
    <div style={{ display:'flex', alignItems:'center', gap:20, flexWrap:'wrap' }}>
      <svg width={size} height={size} viewBox="0 0 110 110">
        <circle cx={cx} cy={cy} r={r} fill="none" stroke={T.border} strokeWidth={14} />
        {slices.map((s, i) => (
          <circle key={i} cx={cx} cy={cy} r={r} fill="none" stroke={s.color} strokeWidth={14}
            strokeDasharray={`${s.dash} ${s.gap}`}
            strokeDashoffset={circ / 4 - s.offset}
            style={{ transform:`rotate(-90deg)`, transformOrigin:'50% 50%' }} />
        ))}
        <text x={cx} y={cy+4} textAnchor="middle" fontSize={11} fontWeight={700} fill={T.navy}>
          {data.length}
        </text>
        <text x={cx} y={cy+16} textAnchor="middle" fontSize={7} fill={T.muted}>sources</text>
      </svg>
      <div style={{ display:'flex', flexDirection:'column', gap:5 }}>
        {data.map((d, i) => {
          const val  = parseFloat(d[valueKey]||0);
          const share = total > 0 ? (val / total * 100).toFixed(1) : 0;
          return (
            <div key={i} style={{ display:'flex', alignItems:'center', gap:7 }}>
              <div style={{ width:10, height:10, borderRadius:2, background:DONUT_COLORS[i%DONUT_COLORS.length], flexShrink:0 }} />
              <span style={{ fontSize:11, color:T.navy, fontWeight:600 }}>{cap(d[labelKey])}</span>
              <span style={{ fontSize:10, color:T.muted }}>{share}%</span>
            </div>
          );
        })}
      </div>
    </div>
  );
}

/* ── Occupancy bar row ────────────────────────────────────────────────────── */
function OccupancyRow({ date, occupied, total, pctVal }) {
  const color = pctVal >= 70 ? T.success : pctVal >= 40 ? T.warning : T.danger;
  return (
    <tr style={{ borderBottom:`1px solid ${T.border}` }}>
      <td style={{ padding:'7px 12px', fontSize:11, color:T.muted, whiteSpace:'nowrap' }}>{fmtD(date)}</td>
      <td style={{ padding:'7px 12px', fontSize:12, fontWeight:700, color:T.navy, textAlign:'right' }}>{occupied}</td>
      <td style={{ padding:'7px 12px', fontSize:11, color:T.muted, textAlign:'right' }}>{total}</td>
      <td style={{ padding:'7px 12px', minWidth:140 }}>
        <div style={{ display:'flex', alignItems:'center', gap:8 }}>
          <div style={{ flex:1, height:8, background:`${color}18`, borderRadius:4 }}>
            <div style={{ width:`${pctVal}%`, height:'100%', background:color, borderRadius:4, transition:'width .4s' }} />
          </div>
          <span style={{ fontSize:11, fontWeight:700, color, width:40, textAlign:'right' }}>{pctVal}%</span>
        </div>
      </td>
    </tr>
  );
}

/* ── Card wrapper ──────────────────────────────────────────────────────────── */
function Card({ children, style }) {
  return (
    <div style={{ background:T.white, border:`1px solid ${T.border}`, borderRadius:14,
      padding:'18px 20px', boxShadow:SHADOW.sm, ...style }}>
      {children}
    </div>
  );
}

/* ── Table helpers ─────────────────────────────────────────────────────────── */
const TH = ({ children, right }) => (
  <th style={{ padding:'9px 12px', fontSize:10, fontWeight:700, color:T.muted,
    textTransform:'uppercase', letterSpacing:.7, textAlign:right?'right':'left',
    background:T.surface, borderBottom:`1px solid ${T.border}`, whiteSpace:'nowrap' }}>
    {children}
  </th>
);
const TD2 = ({ children, right, bold, color }) => (
  <td style={{ padding:'8px 12px', fontSize:12, borderBottom:`1px solid ${T.border}`,
    textAlign:right?'right':'left', fontWeight:bold?700:400, color:color||T.navy }}>
    {children}
  </td>
);

/* ══════════════════════════════════════════════════════════════════
   REPORT TAB COMPONENTS
══════════════════════════════════════════════════════════════════ */

/* ── Daily revenue row with room vs other progress bars ─────────────────── */
function DailyRevenueRow({ date, roomRevenue, otherRevenue, total, maxTotal, currency }) {
  const room  = Number(roomRevenue  || 0);
  const other = Number(otherRevenue || 0);
  const tot   = Number(total ?? room + other);
  const base  = maxTotal > 0 ? maxTotal : 1;
  const roomW  = Math.min(100, (room  / base) * 100);
  const otherW = Math.min(100, (other / base) * 100);

  return (
    <tr style={{ borderBottom:`1px solid ${T.border}` }}>
      <td style={{ padding:'8px 12px', fontSize:11, color:T.muted, whiteSpace:'nowrap' }}>{fmtD(date)}</td>
      <td style={{ padding:'8px 12px', minWidth:160 }}>
        <div style={{ display:'flex', alignItems:'center', gap:8 }}>
          <div style={{ flex:1, height:8, background:`${T.teal}15`, borderRadius:4 }}>
            <div style={{ width:`${roomW}%`, height:'100%', background:T.teal, borderRadius:4, transition:'width .4s' }} />
          </div>
          <span style={{ fontSize:11, fontWeight:700, color:T.teal, minWidth:72, textAlign:'right' }}>
            {fmtMoney(room, currency)}
          </span>
        </div>
      </td>
      <td style={{ padding:'8px 12px', minWidth:160 }}>
        <div style={{ display:'flex', alignItems:'center', gap:8 }}>
          <div style={{ flex:1, height:8, background:`${T.gold}18`, borderRadius:4 }}>
            <div style={{ width:`${otherW}%`, height:'100%', background:T.gold, borderRadius:4, transition:'width .4s' }} />
          </div>
          <span style={{ fontSize:11, fontWeight:700, color:T.gold, minWidth:72, textAlign:'right' }}>
            {fmtMoney(other, currency)}
          </span>
        </div>
      </td>
      <td style={{ padding:'8px 12px', fontSize:12, fontWeight:700, color:T.navy, textAlign:'right', whiteSpace:'nowrap' }}>
        {fmtMoney(tot, currency)}
      </td>
    </tr>
  );
}

/* ── Revenue ────────────────────────────────────────────────────────────────── */
function RevenueReport({ data, currency, from, to }) {
  const summary  = data?.summary || {};
  const daily    = (data?.daily   || []).filter(d =>
    Number(d.roomRevenue || 0) > 0 || Number(d.otherRevenue || 0) > 0
  );
  const byType   = data?.byRoomType || [];
  const cur      = summary.currency || currency;
  const totalRev = Number(summary.totalRevenue || 0);
  const roomRev  = Number(summary.roomRevenue  || 0);
  const otherRev = Number(summary.otherRevenue || 0);
  const maxDaily = daily.reduce((m, d) => Math.max(m, Number(d.total ?? 0) ||
    Number(d.roomRevenue || 0) + Number(d.otherRevenue || 0)), 0);

  const doExport = () => exportCSV(
    ['Date','Room Revenue','Other Revenue','Total'],
    daily.map(d => [d.date, d.roomRevenue, d.otherRevenue, d.total]),
    `revenue-${from}-${to}.csv`
  );

  return (
    <div>
      {/* KPI row — core revenue only */}
      <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill,minmax(200px,1fr))', gap:12, marginBottom:20 }}>
        <KpiCard label="Total Revenue"     value={fmtMoney(totalRev, cur)} icon="💰" highlight />
        <KpiCard label="Room Revenue"      value={fmtMoney(roomRev, cur)}  icon="🛏️" color={T.teal}
          sub={`${pct(roomRev, totalRev)} of total`} />
        <KpiCard label="Other Charges"     value={fmtMoney(otherRev, cur)} icon="🧾" color={T.gold}
          sub={`${pct(otherRev, totalRev)} of total`} />
      </div>

      {/* Revenue split visual */}
      {totalRev > 0 && (
        <Card style={{ marginBottom:16 }}>
          <SectionTitle action={<ExportBtn onClick={doExport} />}>Revenue Breakdown</SectionTitle>
          <div style={{ display:'flex', gap:4, height:20, borderRadius:8, overflow:'hidden', marginBottom:10 }}>
            <div title={`Room Revenue: ${pct(roomRev,totalRev)}`}
              style={{ flex:roomRev, background:T.teal, transition:'flex .5s' }} />
            <div title={`Other: ${pct(otherRev,totalRev)}`}
              style={{ flex:Math.max(otherRev,0), background:T.gold, transition:'flex .5s' }} />
          </div>
          <div style={{ display:'flex', gap:20, flexWrap:'wrap' }}>
            {[['🛏️ Room Revenue', roomRev, T.teal], ['🧾 Other Charges', otherRev, T.gold]].map(([l,v,c])=>(
              <div key={l} style={{ display:'flex', alignItems:'center', gap:8 }}>
                <div style={{ width:10, height:10, borderRadius:2, background:c }} />
                <span style={{ fontSize:11, color:T.muted }}>{l}</span>
                <span style={{ fontSize:12, fontWeight:700, color:T.navy }}>{fmtMoney(v, cur)}</span>
                <span style={{ fontSize:10, color:T.muted }}>({pct(v,totalRev)})</span>
              </div>
            ))}
          </div>
        </Card>
      )}

      {/* Daily breakdown — room vs other with progress comparison */}
      {daily.length > 0 && (
        <Card style={{ marginBottom:16 }}>
          <SectionTitle action={<ExportBtn onClick={doExport} />}>
            Daily Revenue — {from} to {to}
          </SectionTitle>
          <div style={{ display:'flex', gap:16, marginBottom:12, flexWrap:'wrap' }}>
            <div style={{ display:'flex', alignItems:'center', gap:6 }}>
              <div style={{ width:10, height:10, borderRadius:2, background:T.teal }} />
              <span style={{ fontSize:10, color:T.muted }}>Room revenue (bar vs peak day)</span>
            </div>
            <div style={{ display:'flex', alignItems:'center', gap:6 }}>
              <div style={{ width:10, height:10, borderRadius:2, background:T.gold }} />
              <span style={{ fontSize:10, color:T.muted }}>Other charges (bar vs peak day)</span>
            </div>
          </div>
          <div style={{ overflowX:'auto' }}>
            <table style={{ width:'100%', borderCollapse:'collapse' }}>
              <thead><tr>
                <TH>Date</TH>
                <TH>Room Revenue</TH>
                <TH>Other Charges</TH>
                <TH right>Day Total</TH>
              </tr></thead>
              <tbody>
                {daily.map(d => (
                  <DailyRevenueRow key={d.date} date={d.date}
                    roomRevenue={d.roomRevenue} otherRevenue={d.otherRevenue}
                    total={d.total} maxTotal={maxDaily} currency={cur} />
                ))}
                <tr style={{ background:T.surface, fontWeight:700 }}>
                  <td style={{ padding:'9px 12px', fontSize:12 }}>Period total</td>
                  <td style={{ padding:'9px 12px', fontSize:12, fontWeight:700, color:T.teal, textAlign:'right' }}>
                    {fmtMoney(roomRev, cur)}
                  </td>
                  <td style={{ padding:'9px 12px', fontSize:12, fontWeight:700, color:T.gold, textAlign:'right' }}>
                    {fmtMoney(otherRev, cur)}
                  </td>
                  <td style={{ padding:'9px 12px', fontSize:12, fontWeight:700, color:T.navy, textAlign:'right' }}>
                    {fmtMoney(totalRev, cur)}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </Card>
      )}

      {/* By room type */}
      {byType.length > 0 && (
        <Card>
          <SectionTitle
            action={<ExportBtn onClick={() => exportCSV(
              ['Room Type','Stays','Nights Sold','Room Revenue'],
              byType.map(r=>[r.roomTypeName,r.stayCount,r.nightsSold,r.revenue]),
              `revenue-by-roomtype-${from}-${to}.csv`)} />}>
            Revenue by Room Type
          </SectionTitle>
          <div style={{ overflowX:'auto' }}>
            <table style={{ width:'100%', borderCollapse:'collapse' }}>
              <thead><tr>
                <TH>Room Type</TH>
                <TH right>Stays</TH>
                <TH right>Nights Sold</TH>
                <TH right>Room Revenue</TH>
                <TH>Share</TH>
              </tr></thead>
              <tbody>
                {byType.map((r, i) => {
                  const rev = Number(r.revenue||0);
                  return (
                    <tr key={i} style={{ borderBottom:`1px solid ${T.border}` }}>
                      <TD2 bold>{r.roomTypeName || '—'}</TD2>
                      <TD2 right>{fmtNum(r.stayCount)}</TD2>
                      <TD2 right>{fmtNum(r.nightsSold)}</TD2>
                      <TD2 right bold color={T.teal}>{fmtMoney(rev, cur)}</TD2>
                      <td style={{ padding:'8px 12px', minWidth:120 }}>
                        <div style={{ display:'flex', alignItems:'center', gap:8 }}>
                          <div style={{ flex:1, height:6, background:`${T.teal}15`, borderRadius:3 }}>
                            <div style={{ width:`${pct(rev, roomRev)}`, height:'100%',
                              background:T.teal, borderRadius:3 }} />
                          </div>
                          <span style={{ fontSize:10, color:T.muted, width:36 }}>{pct(rev,roomRev)}</span>
                        </div>
                      </td>
                    </tr>
                  );
                })}
                <tr style={{ background:T.surface, fontWeight:700 }}>
                  <td colSpan={3} style={{ padding:'9px 12px', fontSize:12, fontWeight:700 }}>Total</td>
                  <TD2 right bold color={T.teal}>{fmtMoney(roomRev, cur)}</TD2>
                  <TD2 />
                </tr>
              </tbody>
            </table>
          </div>
        </Card>
      )}
    </div>
  );
}

/* ── Occupancy ──────────────────────────────────────────────────────────────── */
function OccupancyReport({ data, from, to }) {
  const summary   = data?.summary || {};
  const daily     = data?.daily   || [];
  const avgOcc    = Number(summary.avgOccupancy  || 0);
  const totalRms  = Number(summary.totalRooms    || 0);
  const avail     = Number(summary.roomsAvailable|| 0);
  const sold      = Number(summary.roomNightsSold|| 0);
  const noShows   = Number(summary.noShows       || 0);
  const occColor  = avgOcc >= 70 ? T.success : avgOcc >= 40 ? T.warning : T.danger;

  // Aggregate status totals from daily data
  const totalCI  = daily.reduce((s, d) => s + Number(d.checkedIn  || 0), 0);
  const totalCO  = daily.reduce((s, d) => s + Number(d.checkedOut || 0), 0);
  const totalCFM = daily.reduce((s, d) => s + Number(d.confirmed  || 0), 0);

  const doExport = () => exportCSV(
    ['Date','Total Rooms','Occupied','Checked-In','Checked-Out','Confirmed','Available','Occupancy %'],
    daily.map(d => [
      d.date, d.totalRooms, d.occupiedRooms,
      d.checkedIn||0, d.checkedOut||0, d.confirmed||0,
      d.available||0, d.occupancyPct,
    ]),
    `occupancy-${from}-${to}.csv`
  );

  return (
    <div>
      {/* KPI row */}
      <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill,minmax(160px,1fr))', gap:12, marginBottom:20 }}>
        <KpiCard label="Avg Occupancy"         value={`${avgOcc.toFixed(1)}%`} icon="📊" color={occColor} highlight />
        <KpiCard label="Total Rooms"           value={fmtNum(totalRms)}        icon="🏨" color={T.navy}
          sub="Property capacity" />
        <KpiCard label="Room Nights Sold"      value={fmtNum(sold)}            icon="✅" color={T.success}
          sub={avail ? `${pct(sold, avail)} utilisation` : ''} />
        <KpiCard label="Checked-In Nights"     value={fmtNum(totalCI)}         icon="🔑" color={T.teal} />
        <KpiCard label="Checked-Out Nights"    value={fmtNum(totalCO)}         icon="🚪" color="#6366F1" />
        <KpiCard label="No-Shows"              value={fmtNum(noShows)}         icon="⛔" color={T.danger} />
      </div>

      {/* Occupancy gauge + daily bar chart */}
      <Card style={{ marginBottom:16 }}>
        <SectionTitle>Period Occupancy Overview</SectionTitle>
        <div style={{ marginBottom:14 }}>
          <div style={{ display:'flex', justifyContent:'space-between', marginBottom:6 }}>
            <span style={{ fontSize:11, color:T.muted }}>0%</span>
            <span style={{ fontSize:13, fontWeight:700, color:occColor }}>{avgOcc.toFixed(1)}% avg occupancy</span>
            <span style={{ fontSize:11, color:T.muted }}>100%</span>
          </div>
          <div style={{ height:14, background:`${occColor}15`, borderRadius:8 }}>
            <div style={{ width:`${Math.min(avgOcc, 100)}%`, height:'100%',
              background:`linear-gradient(90deg,${occColor}80,${occColor})`,
              borderRadius:8, transition:'width .6s ease' }} />
          </div>
          <div style={{ display:'flex', gap:14, marginTop:8 }}>
            {[['< 40%','Low',T.danger],['40–70%','Moderate',T.warning],['>70%','High',T.success]].map(([r,lbl,c])=>(
              <div key={lbl} style={{ display:'flex', alignItems:'center', gap:4 }}>
                <div style={{ width:8, height:8, borderRadius:2, background:c }} />
                <span style={{ fontSize:10, color:T.muted }}>{r} {lbl}</span>
              </div>
            ))}
          </div>
        </div>
        {/* Bar chart: occupied vs total */}
        {daily.length > 0 && (
          <BarChart
            data={daily.map(d => ({
              date: d.date,
              occupied: Number(d.occupiedRooms || 0),
              total: Number(d.totalRooms || 0),
            }))}
            labelKey="date"
            valueKey="occupied"
            color={occColor}
            height={140}
          />
        )}
      </Card>

      {/* Daily detail table */}
      {daily.length > 0 && (
        <Card>
          <SectionTitle action={<ExportBtn onClick={doExport} />}>
            Daily Breakdown — {from} to {to}
          </SectionTitle>
          <div style={{ overflowX:'auto' }}>
            <table style={{ width:'100%', borderCollapse:'collapse' }}>
              <thead><tr>
                <TH>Date</TH>
                <TH right>Total</TH>
                <TH right>Occupied</TH>
                <TH right style={{ color:T.teal }}>Checked-In</TH>
                <TH right style={{ color:'#6366F1' }}>Checked-Out</TH>
                <TH right style={{ color:T.warning }}>Confirmed</TH>
                <TH right>Available</TH>
                <TH>Occupancy</TH>
              </tr></thead>
              <tbody>
                {daily.map((d, i) => {
                  const occ  = Number(d.occupiedRooms || 0);
                  const tot  = Number(d.totalRooms    || 0);
                  const pctV = parseFloat(d.occupancyPct || 0);
                  const pctC = occColor => pctV >= 70 ? T.success : pctV >= 40 ? T.warning : T.danger;
                  return (
                    <tr key={i} style={{ borderBottom:`1px solid ${T.border}`,
                      background: i%2===0 ? T.white : `${T.surface}80` }}>
                      <TD2><span style={{ fontFamily:FONTS.mono, fontSize:11 }}>{d.date}</span></TD2>
                      <TD2 right><span style={{ color:T.muted }}>{tot}</span></TD2>
                      <TD2 right bold>{occ}</TD2>
                      <TD2 right><span style={{ color:T.teal, fontWeight:600 }}>{d.checkedIn  || 0}</span></TD2>
                      <TD2 right><span style={{ color:'#6366F1', fontWeight:600 }}>{d.checkedOut || 0}</span></TD2>
                      <TD2 right><span style={{ color:T.warning, fontWeight:600 }}>{d.confirmed  || 0}</span></TD2>
                      <TD2 right><span style={{ color:T.muted }}>{d.available || (tot - occ)}</span></TD2>
                      <td style={{ padding:'7px 12px', minWidth:140 }}>
                        <div style={{ display:'flex', alignItems:'center', gap:8 }}>
                          <div style={{ flex:1, height:6, background:`${T.border}`, borderRadius:3, overflow:'hidden' }}>
                            <div style={{
                              width:`${Math.min(100, pctV)}%`, height:'100%', borderRadius:3,
                              background: pctV >= 70 ? T.success : pctV >= 40 ? T.warning : T.danger,
                              transition:'width .4s'
                            }} />
                          </div>
                          <span style={{ fontSize:10, fontWeight:700, color:T.navy, width:38, textAlign:'right' }}>
                            {pctV.toFixed(1)}%
                          </span>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </Card>
      )}
    </div>
  );
}

/* ── Channels ───────────────────────────────────────────────────────────────── */
function ChannelReport({ data, currency, from, to }) {
  const channels = Array.isArray(data) ? data : (data?.channels || []);
  if (!channels.length) return (
    <div style={{ textAlign:'center', padding:48, color:T.muted }}>
      <p style={{ fontSize:28, marginBottom:8 }}>🌐</p>
      <p style={{ fontSize:13 }}>No booking data for this period</p>
    </div>
  );
  const totalRev   = channels.reduce((s, c) => s + Number(c.revenue||0), 0);
  const totalResv  = channels.reduce((s, c) => s + Number(c.reservations||0), 0);

  const doExport = () => exportCSV(
    ['Channel','Reservations','% Reservations','Revenue','% Revenue'],
    channels.map(c => [
      c.channel, c.reservations,
      totalResv ? (c.reservations/totalResv*100).toFixed(1)+'%' : '0%',
      c.revenue,
      totalRev  ? (c.revenue/totalRev*100).toFixed(1)+'%' : '0%',
    ]),
    `channels-${from}-${to}.csv`
  );

  return (
    <div>
      <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill,minmax(180px,1fr))', gap:12, marginBottom:20 }}>
        <KpiCard label="Total Reservations" value={fmtNum(totalResv)}        icon="📋" color={T.navy} highlight />
        <KpiCard label="Total Revenue"      value={fmtMoney(totalRev,currency)} icon="💰" color={T.teal} />
        <KpiCard label="Active Channels"    value={channels.length}           icon="🌐" color={T.gold} />
      </div>

      <div style={{ display:'grid', gridTemplateColumns:'auto 1fr', gap:16, alignItems:'start', marginBottom:16 }}>
        <Card style={{ minWidth:220 }}>
          <SectionTitle>Channel Mix</SectionTitle>
          <DonutChart data={channels} valueKey="reservations" labelKey="channel" />
        </Card>
        <Card>
          <SectionTitle action={<ExportBtn onClick={doExport} />}>Channel Performance</SectionTitle>
          <div style={{ overflowX:'auto' }}>
            <table style={{ width:'100%', borderCollapse:'collapse' }}>
              <thead><tr>
                <TH>Channel</TH>
                <TH right>Reservations</TH>
                <TH>Reservation Share</TH>
                <TH right>Revenue</TH>
                <TH>Revenue Share</TH>
              </tr></thead>
              <tbody>
                {channels.map((c, i) => {
                  const resvShare  = totalResv > 0 ? c.reservations / totalResv * 100 : 0;
                  const revShare   = totalRev  > 0 ? Number(c.revenue||0) / totalRev * 100 : 0;
                  const barColor   = DONUT_COLORS[i % DONUT_COLORS.length];
                  return (
                    <tr key={i} style={{ borderBottom:`1px solid ${T.border}` }}>
                      <TD2><span style={{ fontWeight:700 }}>{cap(c.channel)}</span></TD2>
                      <TD2 right>{fmtNum(c.reservations)}</TD2>
                      <td style={{ padding:'8px 12px', minWidth:130 }}>
                        <div style={{ display:'flex', alignItems:'center', gap:6 }}>
                          <div style={{ flex:1, height:7, background:`${barColor}18`, borderRadius:4 }}>
                            <div style={{ width:`${resvShare}%`, height:'100%', background:barColor, borderRadius:4 }} />
                          </div>
                          <span style={{ fontSize:10, color:T.muted, width:36, textAlign:'right' }}>{resvShare.toFixed(1)}%</span>
                        </div>
                      </td>
                      <TD2 right bold color={T.teal}>{fmtMoney(c.revenue, currency)}</TD2>
                      <td style={{ padding:'8px 12px', minWidth:130 }}>
                        <div style={{ display:'flex', alignItems:'center', gap:6 }}>
                          <div style={{ flex:1, height:7, background:`${T.teal}18`, borderRadius:4 }}>
                            <div style={{ width:`${revShare}%`, height:'100%', background:T.teal, borderRadius:4 }} />
                          </div>
                          <span style={{ fontSize:10, color:T.muted, width:36, textAlign:'right' }}>{revShare.toFixed(1)}%</span>
                        </div>
                      </td>
                    </tr>
                  );
                })}
                <tr style={{ background:T.surface }}>
                  <TD2 bold>Total</TD2>
                  <TD2 right bold>{fmtNum(totalResv)}</TD2>
                  <TD2 />
                  <TD2 right bold color={T.teal}>{fmtMoney(totalRev, currency)}</TD2>
                  <TD2 />
                </tr>
              </tbody>
            </table>
          </div>
        </Card>
      </div>
    </div>
  );
}

/* ── Folio ──────────────────────────────────────────────────────────────────── */
function FolioReport({ data, currency, from, to }) {
  const [filter, setFilter] = useState('all');
  const items    = Array.isArray(data) ? data : (data?.items || []);
  const summary  = data?.summary || {};
  const cur      = summary.currency || currency;
  const billed   = Number(summary.totalBilled      || 0);
  const collected= Number(summary.totalCollected   || 0);
  const outstnd  = Number(summary.totalOutstanding || 0);
  const roomC    = Number(summary.totalRoomCharges || 0);
  const otherC   = Number(summary.totalOtherCharges|| 0);

  const filtered = items.filter(it => {
    const bal = Number(it.balance||0);
    const billable = it.isBillable !== false && !it.isNoShow && it.status !== 'cancelled';
    if (filter === 'outstanding') return billable && bal > 0.01;
    if (filter === 'settled')     return billable && bal <= 0.01;
    return true;
  });

  const isBillableRow = it =>
    it.isBillable !== false && !it.isNoShow && it.status !== 'cancelled';

  const billableRows = filtered.filter(isBillableRow);
  const excludedCount = filtered.length - billableRows.length;

  const doExport = () => exportCSV(
    ['Conf #','Guest','Room','Check-in','Check-out','Nights','Room Charges','Other Charges','Total','Paid','Balance','Status'],
    filtered.map(it => [
      it.confirmationNumber, it.guestName,
      it.roomNumber ? `Rm ${it.roomNumber}` : (it.roomTypeName||'—'),
      it.arrivalDate, it.departureDate, it.nights||0,
      it.roomCharges||0, it.otherCharges||0, it.totalCharges||0, it.totalPaid||0, it.balance||0, it.status,
    ]),
    `folio-${from}-${to}.csv`
  );

  return (
    <div>
      {/* Summary KPIs */}
      <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill,minmax(180px,1fr))', gap:12, marginBottom:16 }}>
        <KpiCard label="Total Billed"      value={fmtMoney(billed,    cur)} icon="🧾" color={T.navy} highlight />
        <KpiCard label="Total Collected"   value={fmtMoney(collected, cur)} icon="✅" color={T.success}
          sub={`${pct(collected,billed)} collected`} />
        <KpiCard label="Outstanding"       value={fmtMoney(outstnd,   cur)} icon="⚠️" color={T.danger}
          sub={outstnd > 0 ? 'Needs attention' : 'All settled'} />
        <KpiCard label="Room Charges"      value={fmtMoney(roomC,     cur)} icon="🛏️" color={T.teal}
          sub={`${pct(roomC,billed)} of total`} />
        <KpiCard label="Other Charges"     value={fmtMoney(otherC,    cur)} icon="🍽️" color={T.gold}
          sub={`F&B, extras & fees`} />
        <KpiCard label="Reservations"      value={fmtNum(summary.billableCount ?? billableRows.length)} icon="📋" color={T.slate}
          sub={excludedCount > 0 ? `${summary.noShowCount||0} no-show, ${summary.cancelledCount||0} cancelled excluded` : undefined} />
      </div>

      {/* Charge split bar */}
      {billed > 0 && (
        <Card style={{ marginBottom:16 }}>
          <SectionTitle>Charge Composition</SectionTitle>
          <div style={{ display:'flex', gap:3, height:14, borderRadius:7, overflow:'hidden', marginBottom:8 }}>
            <div style={{ flex:Math.max(roomC,1), background:T.teal }} />
            <div style={{ flex:Math.max(otherC,0), background:T.gold }} />
          </div>
          <div style={{ display:'flex', gap:24, flexWrap:'wrap' }}>
            {[['🛏️ Room Revenue',roomC,T.teal],['🍽️ Other Charges',otherC,T.gold]].map(([l,v,c])=>(
              <div key={l} style={{ display:'flex', alignItems:'center', gap:7 }}>
                <div style={{ width:10, height:10, borderRadius:2, background:c }} />
                <span style={{ fontSize:11, color:T.muted }}>{l}</span>
                <span style={{ fontSize:12, fontWeight:700, color:T.navy }}>{fmtMoney(v, cur)}</span>
                <span style={{ fontSize:10, color:T.muted }}>({pct(v, billed)})</span>
              </div>
            ))}
          </div>
        </Card>
      )}

      {/* Collection progress */}
      {billed > 0 && (
        <Card style={{ marginBottom:16 }}>
          <SectionTitle>Collection Progress</SectionTitle>
          <div style={{ display:'flex', justifyContent:'space-between', marginBottom:4 }}>
            <span style={{ fontSize:11, color:T.muted }}>Collected</span>
            <span style={{ fontSize:12, fontWeight:700, color:T.success }}>{pct(collected, billed)}</span>
          </div>
          <div style={{ height:12, background:`${T.success}15`, borderRadius:6 }}>
            <div style={{ width:`${Math.min(collected/billed*100, 100)}%`, height:'100%',
              background:`linear-gradient(90deg,${T.success}80,${T.success})`,
              borderRadius:6, transition:'width .6s' }} />
          </div>
          <div style={{ display:'flex', justifyContent:'space-between', marginTop:4 }}>
            <span style={{ fontSize:10, color:T.muted }}>{fmtMoney(collected, cur)}</span>
            <span style={{ fontSize:10, color:T.danger }}>{fmtMoney(outstnd, cur)} outstanding</span>
          </div>
        </Card>
      )}

      {/* Filter + table */}
      <Card>
        <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:12, flexWrap:'wrap', gap:8 }}>
          <div style={{ display:'flex', gap:6 }}>
            {[['all','All'],['outstanding','Outstanding'],['settled','Settled']].map(([v,l]) => (
              <button key={v} onClick={()=>setFilter(v)} style={{
                padding:'5px 12px', borderRadius:8, border:`1px solid ${filter===v?T.teal:T.border}`,
                background:filter===v?T.teal:T.surface, color:filter===v?'#fff':T.slate,
                fontSize:11, fontWeight:700, cursor:'pointer', fontFamily:FONTS.body,
              }}>{l}</button>
            ))}
          </div>
          <ExportBtn onClick={doExport} />
        </div>
        <div style={{ overflowX:'auto' }}>
          <table style={{ width:'100%', borderCollapse:'collapse' }}>
            <thead><tr>
              <TH>Guest</TH>
              <TH>Conf #</TH>
              <TH>Room</TH>
              <TH>Period</TH>
              <TH right>Room Charges</TH>
              <TH right>Other Charges</TH>
              <TH right>Total</TH>
              <TH right>Paid</TH>
              <TH right>Balance</TH>
              <TH>Status</TH>
            </tr></thead>
            <tbody>
              {filtered.length === 0 ? (
                <tr><td colSpan={10} style={{ textAlign:'center', padding:24, color:T.muted, fontSize:12 }}>No records</td></tr>
              ) : filtered.map((it, i) => {
                const bal       = Number(it.balance||0);
                const roomCh    = Number(it.roomCharges||0);
                const otherCh   = Number(it.otherCharges||0);
                const totalCh   = Number(it.totalCharges||0);
                const paid      = Number(it.totalPaid||0);
                const isOutst   = bal > 0.01;
                const roomLabel = it.roomNumber
                  ? `Rm ${it.roomNumber}` : (it.roomTypeName||'—');
                const isExcluded = !isBillableRow(it);
                return (
                  <tr key={i} style={{ borderBottom:`1px solid ${T.border}`,
                    background: isExcluded ? `${T.muted}08` : isOutst ? `${T.danger}04` : undefined,
                    opacity: isExcluded ? 0.7 : 1 }}>
                    <TD2 bold>{it.guestName||'—'}</TD2>
                    <td style={{ padding:'8px 12px', fontFamily:FONTS.mono, fontSize:10, borderBottom:`1px solid ${T.border}`, color:T.muted }}>
                      {it.confirmationNumber||'—'}
                    </td>
                    <TD2>{roomLabel}</TD2>
                    <td style={{ padding:'8px 12px', fontSize:11, color:T.muted, borderBottom:`1px solid ${T.border}`, whiteSpace:'nowrap' }}>
                      {fmtD(it.arrivalDate)} → {fmtD(it.departureDate)}
                    </td>
                    <TD2 right>{fmtMoney(roomCh, cur)}</TD2>
                    <TD2 right color={otherCh > 0 ? T.gold : T.muted}>{fmtMoney(otherCh, cur)}</TD2>
                    <TD2 right bold>{fmtMoney(totalCh, cur)}</TD2>
                    <TD2 right color={T.success}>{fmtMoney(paid, cur)}</TD2>
                    <TD2 right bold color={isOutst ? T.danger : T.success}>
                      {isOutst ? fmtMoney(bal, cur) : '✓ Settled'}
                    </TD2>
                    <td style={{ padding:'8px 12px', borderBottom:`1px solid ${T.border}` }}>
                      <Badge label={cap(it.status)} color={
                        it.status==='checked_out'?T.muted:it.status==='checked_in'?T.success:
                        it.status==='cancelled'?T.danger:T.teal} />
                    </td>
                  </tr>
                );
              })}
            </tbody>
            {billableRows.length > 0 && (
              <tfoot>
                <tr style={{ background:T.surface, borderTop:`2px solid ${T.border}` }}>
                  <td colSpan={4} style={{ padding:'10px 12px', fontSize:12, fontWeight:700 }}>
                    Totals ({billableRows.length} billable{excludedCount > 0
                      ? `, ${excludedCount} cancelled/no-show excluded` : ''})
                  </td>
                  <td style={{ padding:'10px 12px', textAlign:'right', fontWeight:700, fontSize:12 }}>
                    {fmtMoney(billableRows.reduce((s,it)=>s+Number(it.roomCharges||0),0), cur)}
                  </td>
                  <td style={{ padding:'10px 12px', textAlign:'right', fontWeight:700, fontSize:12 }}>
                    {fmtMoney(billableRows.reduce((s,it)=>s+Number(it.otherCharges||0),0), cur)}
                  </td>
                  <td style={{ padding:'10px 12px', textAlign:'right', fontWeight:700, fontSize:12, color:T.navy }}>
                    {fmtMoney(billableRows.reduce((s,it)=>s+Number(it.totalCharges||0),0), cur)}
                  </td>
                  <td style={{ padding:'10px 12px', textAlign:'right', fontWeight:700, fontSize:12, color:T.success }}>
                    {fmtMoney(billableRows.reduce((s,it)=>s+Number(it.totalPaid||0),0), cur)}
                  </td>
                  <td style={{ padding:'10px 12px', textAlign:'right', fontWeight:700, fontSize:12,
                    color: billableRows.reduce((s,it)=>s+Math.max(Number(it.balance||0),0),0) > 0 ? T.danger : T.success }}>
                    {fmtMoney(billableRows.reduce((s,it)=>s+Math.max(Number(it.balance||0),0),0), cur)}
                  </td>
                  <td />
                </tr>
              </tfoot>
            )}
          </table>
        </div>
      </Card>
    </div>
  );
}

/* ── Housekeeping ───────────────────────────────────────────────────────────── */
function HKReport({ data, from, to }) {
  const tasks  = Array.isArray(data) ? data : (data?.tasks || []);
  const stats  = data?.stats || {};
  const SCOL   = { pending:T.warning, in_progress:T.teal, completed:T.success, inspected:T.navy };
  const total  = Object.values(stats).reduce((s,v)=>s+Number(v),0);

  const doExport = () => exportCSV(
    ['Date','Room','Room Type','Task Type','Priority','Assigned To','Status','Started At','Completed At'],
    tasks.map(t=>[t.scheduledDate||from, t.roomNumber ? `Rm ${t.roomNumber}` : '—',
      t.roomTypeName||'—', t.taskType, t.priority,
      t.assignedName||'Unassigned', t.status, t.startedAt||'', t.completedAt||'']),
    `housekeeping-${from}-${to}.csv`
  );

  return (
    <div>
      {/* Stats */}
      <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill,minmax(150px,1fr))', gap:12, marginBottom:16 }}>
        {Object.entries(stats).map(([k,v]) => (
          <KpiCard key={k} label={cap(k)} value={fmtNum(v)} icon={
            k==='completed'?'✅':k==='pending'?'⏳':k==='in_progress'?'🧹':'🔍'}
            color={SCOL[k]||T.navy}
            sub={total > 0 ? `${(Number(v)/total*100).toFixed(0)}% of tasks` : ''} />
        ))}
        <KpiCard label="Total Tasks" value={fmtNum(total)} icon="📋" color={T.navy} />
      </div>

      {/* Completion bar */}
      {total > 0 && (
        <Card style={{ marginBottom:16 }}>
          <SectionTitle>Task Status Distribution</SectionTitle>
          <div style={{ display:'flex', gap:2, height:14, borderRadius:7, overflow:'hidden', marginBottom:8 }}>
            {Object.entries(stats).map(([k,v]) => (
              <div key={k} style={{ flex:Number(v), background:SCOL[k]||T.muted, transition:'flex .4s' }} />
            ))}
          </div>
          <div style={{ display:'flex', gap:16, flexWrap:'wrap' }}>
            {Object.entries(stats).map(([k,v]) => (
              <div key={k} style={{ display:'flex', alignItems:'center', gap:5 }}>
                <div style={{ width:10, height:10, borderRadius:2, background:SCOL[k]||T.muted }} />
                <span style={{ fontSize:10, color:T.muted }}>{cap(k)}</span>
                <span style={{ fontSize:11, fontWeight:700, color:T.navy }}>{v}</span>
              </div>
            ))}
          </div>
        </Card>
      )}

      {tasks.length > 0 && (
        <Card>
          <SectionTitle action={<ExportBtn onClick={doExport} />}>
            Tasks — {from}{from !== to ? ` to ${to}` : ''}
          </SectionTitle>
          <div style={{ overflowX:'auto' }}>
            <table style={{ width:'100%', borderCollapse:'collapse' }}>
              <thead><tr>
                <TH>Room</TH>
                <TH>Room Type</TH>
                <TH>Task</TH>
                <TH>Priority</TH>
                <TH>Assigned To</TH>
                <TH>Status</TH>
                <TH>Completed</TH>
              </tr></thead>
              <tbody>
                {tasks.map((t, i) => (
                  <tr key={i} style={{ borderBottom:`1px solid ${T.border}`,
                    background: i%2===0 ? T.white : `${T.surface}80` }}>
                    <TD2 bold>
                      {t.roomNumber ? `Rm ${t.roomNumber}` : '—'}
                    </TD2>
                    <TD2 color={T.muted}>{t.roomTypeName || '—'}</TD2>
                    <TD2>{cap(t.taskType)}</TD2>
                    <td style={{ padding:'8px 12px', borderBottom:`1px solid ${T.border}` }}>
                      <Badge label={cap(t.priority||'normal')} color={
                        t.priority==='urgent'?T.danger:t.priority==='high'?T.warning:T.teal} />
                    </td>
                    <TD2>{t.assignedName || <span style={{ color:T.muted, fontSize:10 }}>Unassigned</span>}</TD2>
                    <td style={{ padding:'8px 12px', borderBottom:`1px solid ${T.border}` }}>
                      <Badge label={cap(t.status)} color={SCOL[t.status]||T.muted} />
                    </td>
                    <TD2 color={T.muted}>{t.completedAt ? fmtD(t.completedAt.slice(0,10)) : '—'}</TD2>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}
    </div>
  );
}

/* ── Manifests ──────────────────────────────────────────────────────────────── */
function ManifestReport({ data, from, to, currency }) {
  const [view, setView] = useState('arrivals');
  // Range response shape: { arrivals: [...], departures: [...] }
  const arrivals    = data?.arrivals   || (Array.isArray(data?.[0]) ? data[0] : []);
  const departures  = data?.departures || (Array.isArray(data?.[1]) ? data[1] : []);
  const cur         = currency;
  const list        = view === 'arrivals' ? arrivals : departures;

  const doExport = () => exportCSV(
    view === 'arrivals'
      ? ['Date','Conf #','Guest','Nationality','Room','Nights','Adults','Children','Meal Plan','Source','Special Requests']
      : ['Date','Conf #','Guest','Room','Check-out','Balance','Status'],
    list.map(r => view === 'arrivals'
      ? [r.arrivalDate, r.confirmationNumber, r.guestName, r.guestNationality||'',
         r.roomNumber||'—', (r.numNights||''), r.adults||1, r.children||0,
         r.mealPlan||'room_only', r.source||'direct', r.specialRequests||'']
      : [r.departureDate||r.arrivalDate, r.confirmationNumber, r.guestName,
         r.roomNumber||'—', r.departureDate, r.balance||0, r.status]),
    `manifest-${view}-${from}-${to}.csv`
  );

  return (
    <div>
      <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12, marginBottom:16, maxWidth:400 }}>
        <KpiCard label="Arrivals"   value={arrivals.length}   icon="🛎️" color={T.success} />
        <KpiCard label="Departures" value={departures.length} icon="🚪" color={T.warning} />
      </div>

      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:12, flexWrap:'wrap', gap:8 }}>
        <div style={{ display:'flex', gap:6 }}>
          {[['arrivals','🛎️ Arrivals'],['departures','🚪 Departures']].map(([v,l]) => (
            <button key={v} onClick={()=>setView(v)} style={{
              padding:'7px 16px', borderRadius:9, border:`1.5px solid ${view===v?T.teal:T.border}`,
              background:view===v?T.teal:T.surface, color:view===v?'#fff':T.slate,
              fontSize:12, fontWeight:700, cursor:'pointer', fontFamily:FONTS.body,
            }}>{l} ({v==='arrivals'?arrivals.length:departures.length})</button>
          ))}
        </div>
        <ExportBtn onClick={doExport} />
      </div>

      <Card>
        <div style={{ overflowX:'auto' }}>
          {list.length === 0 ? (
            <p style={{ textAlign:'center', padding:32, color:T.muted, fontSize:12 }}>
              No {view} for this date range
            </p>
          ) : view === 'arrivals' ? (
            <table style={{ width:'100%', borderCollapse:'collapse' }}>
              <thead><tr>
                <TH>Arrival Date</TH>
                <TH>Guest</TH>
                <TH>Conf #</TH>
                <TH>Nationality</TH>
                <TH>Room</TH>
                <TH right>Pax</TH>
                <TH>Meal</TH>
                <TH>Source</TH>
                <TH>Requests</TH>
              </tr></thead>
              <tbody>
                {list.map((r, i) => (
                  <tr key={i} style={{ borderBottom:`1px solid ${T.border}` }}>
                    <TD2 color={T.muted}>{fmtD(r.arrivalDate)}</TD2>
                    <TD2 bold>{r.guestName || '—'}</TD2>
                    <td style={{ padding:'8px 12px', fontFamily:FONTS.mono, fontSize:10,
                      borderBottom:`1px solid ${T.border}`, color:T.muted }}>{r.confirmationNumber}</td>
                    <TD2>{r.guestNationality || '—'}</TD2>
                    <TD2>{r.roomNumber ? `Rm ${r.roomNumber}` : '—'}</TD2>
                    <TD2 right>{(r.adults||1)+(r.children||0)}</TD2>
                    <TD2>{cap(r.mealPlan||'room_only')}</TD2>
                    <TD2>{cap(r.source||'direct')}</TD2>
                    <td style={{ padding:'8px 12px', maxWidth:160, fontSize:11, color:T.muted,
                      borderBottom:`1px solid ${T.border}`, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>
                      {r.specialRequests || <span style={{ color:T.border }}>—</span>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <table style={{ width:'100%', borderCollapse:'collapse' }}>
              <thead><tr>
                <TH>Departure Date</TH>
                <TH>Guest</TH>
                <TH>Conf #</TH>
                <TH>Room</TH>
                <TH right>Balance</TH>
                <TH>Status</TH>
              </tr></thead>
              <tbody>
                {list.map((r, i) => {
                  const bal = Number(r.balance||0);
                  return (
                    <tr key={i} style={{ borderBottom:`1px solid ${T.border}`,
                      background: bal > 0.01 ? `${T.danger}03` : undefined }}>
                      <TD2 color={T.muted}>{fmtD(r.departureDate||r.arrivalDate)}</TD2>
                      <TD2 bold>{r.guestName || '—'}</TD2>
                      <td style={{ padding:'8px 12px', fontFamily:FONTS.mono, fontSize:10,
                        borderBottom:`1px solid ${T.border}`, color:T.muted }}>{r.confirmationNumber}</td>
                      <TD2>{r.roomNumber ? `Rm ${r.roomNumber}` : '—'}</TD2>
                      <TD2 right bold color={bal > 0.01 ? T.danger : T.success}>
                        {bal > 0.01 ? fmtMoney(bal, cur) : '✓ Clear'}
                      </TD2>
                      <td style={{ padding:'8px 12px', borderBottom:`1px solid ${T.border}` }}>
                        <Badge label={cap(r.status)} color={
                          r.status==='checked_in'?T.success:r.status==='checked_out'?T.muted:T.teal} />
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      </Card>
    </div>
  );
}

/* ══════════════════════════════════════════════════════════════════
   MAIN REPORTS PAGE
══════════════════════════════════════════════════════════════════ */
const TABS = [
  { id:'revenue',      label:'Revenue',      icon:'💰', desc:'Income & breakdown' },
  { id:'occupancy',    label:'Occupancy',    icon:'📊', desc:'Room utilisation' },
  { id:'channel',      label:'Channels',     icon:'🌐', desc:'Booking sources' },
  { id:'folio',        label:'Folio',        icon:'🧾', desc:'Charges & payments' },
  { id:'housekeeping', label:'Housekeeping', icon:'🧹', desc:'Tasks & status' },
  { id:'manifest',     label:'Manifests',    icon:'📋', desc:'Arrivals & departures' },
];

const PRESETS = [
  { label:'Today',     from:today,         to:today },
  { label:'7 Days',    from:daysAgo(6),    to:today },
  { label:'This Month',from:monthStart,    to:today },
  { label:'30 Days',   from:daysAgo(29),   to:today },
  { label:'90 Days',   from:daysAgo(89),   to:today },
];

export default function Reports({ property }) {
  const [tab,     setTab]     = useState('revenue');
  const [from,    setFrom]    = useState(monthStart);
  const [to,      setTo]      = useState(today);
  const [data,    setData]    = useState(null);
  const [loading, setLoading] = useState(false);
  const [error,   setError]   = useState('');

  const { pendingCount, hasPending } = usePendingSync();
  const pid      = property?.id;
  const currency = property?.currency || 'RWF';
  const activeTab = TABS.find(t => t.id === tab);

  const runReport = useCallback(async () => {
    if (!pid) return;
    setLoading(true); setError(''); setData(null);
    try {
      let result;
      switch (tab) {
        case 'revenue':      result = await fetchRevenueSummary(pid, from, to);    break;
        case 'occupancy':    result = await fetchOccupancyReport(pid, from, to);   break;
        case 'channel':      result = await fetchChannelReport(pid, from, to);     break;
        case 'folio':        result = await fetchFolioReport(pid, from, to);       break;
        case 'housekeeping': result = await fetchHKReport(pid, from, to);          break;
        case 'manifest': {
          // Use allSettled so one list can succeed even if the other is unavailable
          const [arrRes, depRes] = await Promise.allSettled([
            fetchArrivalManifest(pid, from, to),
            fetchDepartureManifest(pid, from, to),
          ]);
          const unpack = r => {
            if (r.status !== 'fulfilled') return [];
            const d = r.value;
            return d?.arrivals ?? d?.departures ?? (Array.isArray(d) ? d : []);
          };
          result = {
            arrivals:   unpack(arrRes),
            departures: unpack(depRes),
            _partial:   arrRes.status !== 'fulfilled' || depRes.status !== 'fulfilled',
          };
          if (result._partial) setError('⚠️ Some manifest data could not be loaded (may be offline).');
          break;
        }
        default: result = null;
      }
      setData(result);
    } catch (e) { setError(e.message); }
    finally { setLoading(false); }
  }, [pid, tab, from, to]);

  // Re-run after sync restores connection so cached report refreshes automatically
  useOfflineInvalidation('reports', runReport);

  const renderReport = () => {
    if (loading) return (
      <div style={{ display:'flex', flexDirection:'column', alignItems:'center', padding:60, gap:14 }}>
        <div style={{ width:36, height:36, border:`3px solid ${T.border}`, borderTopColor:T.teal,
          borderRadius:'50%', animation:'spin 1s linear infinite' }} />
        <p style={{ fontSize:13, color:T.muted }}>Generating report…</p>
      </div>
    );
    if (error && !data) return (
      <div style={{ padding:'16px 20px', background:T.dangerPale, borderRadius:10,
        border:`1px solid ${T.danger}30`, color:T.danger, fontSize:13 }}>
        ❌ {error}
      </div>
    );
    if (!data) return (
      <div style={{ textAlign:'center', padding:60 }}>
        <p style={{ fontSize:32, marginBottom:12 }}>📊</p>
        <p style={{ fontSize:14, color:T.navy, fontWeight:600, marginBottom:4 }}>Ready to Generate</p>
        <p style={{ fontSize:12, color:T.muted }}>Select a date range above and click Generate Report</p>
      </div>
    );
      switch (tab) {
      case 'revenue':      return <RevenueReport      data={data} currency={currency} from={from} to={to} />;
      case 'occupancy':    return <OccupancyReport    data={data} from={from} to={to} />;
      case 'channel':      return <ChannelReport      data={data} currency={currency} from={from} to={to} />;
      case 'folio':        return <FolioReport        data={data} currency={currency} from={from} to={to} />;
      case 'housekeeping': return <HKReport           data={data} from={from} to={to} />;
      case 'manifest':     return <ManifestReport     data={data} from={from} to={to} currency={currency} />;
      default: return null;
    }
  };

  return (
    <div className="fade-up">
      <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>

      {/* ── Page header ── */}
      <div style={{ marginBottom:20 }}>
        <h1 style={{ fontFamily:FONTS.display, fontSize:26, fontWeight:700, color:T.navy, lineHeight:1 }}>
          Reports & Analytics
        </h1>
        <p style={{ fontSize:12, color:T.muted, marginTop:4 }}>
          {property?.name} · Financial and operational intelligence
        </p>
      </div>

      {/* ── Pending offline changes notice ── */}
      {hasPending && (
        <div style={{
          background:`${T.gold}14`, border:`1px solid ${T.gold}44`,
          borderRadius:10, padding:'9px 16px', marginBottom:14,
          display:'flex', alignItems:'center', gap:10, fontSize:12, color:'#92400e',
        }}>
          <span style={{ fontSize:16 }}>⏳</span>
          <span>
            <strong>{pendingCount} offline change{pendingCount > 1 ? 's' : ''} pending sync</strong> —
            report figures below will update automatically after synchronisation completes.
          </span>
        </div>
      )}

      {/* ── Tab bar ── */}
      <div style={{ display:'flex', gap:4, marginBottom:16, overflowX:'auto', paddingBottom:2 }}>
        {TABS.map(t => (
          <button key={t.id} onClick={()=>{ setTab(t.id); setData(null); }} style={{
            display:'flex', alignItems:'center', gap:7, padding:'9px 16px',
            borderRadius:10, border:`1.5px solid ${tab===t.id?T.teal:T.border}`,
            background:tab===t.id?T.teal:T.white,
            color:tab===t.id?'#fff':T.slate,
            cursor:'pointer', fontFamily:FONTS.body, fontSize:12, fontWeight:700,
            whiteSpace:'nowrap', transition:'all .15s', flexShrink:0,
          }}>
            <span>{t.icon}</span>{t.label}
          </button>
        ))}
      </div>

      {/* ── Controls panel ── */}
      <div style={{ background:T.white, border:`1px solid ${T.border}`, borderRadius:14,
        padding:'14px 18px', marginBottom:16, boxShadow:SHADOW.sm }}>
        <div style={{ display:'flex', alignItems:'flex-end', gap:12, flexWrap:'wrap' }}>
          <div>
            <label style={{ fontSize:9, fontWeight:700, color:T.muted, letterSpacing:.8,
              textTransform:'uppercase', display:'block', marginBottom:4 }}>From</label>
            <input type="date" value={from} onChange={e=>setFrom(e.target.value)}
              style={{ border:`1.5px solid ${T.border}`, borderRadius:9, padding:'8px 11px',
                fontSize:12, fontFamily:FONTS.body, color:T.navy, outline:'none', width:155 }} />
          </div>
          <div>
            <label style={{ fontSize:9, fontWeight:700, color:T.muted, letterSpacing:.8,
              textTransform:'uppercase', display:'block', marginBottom:4 }}>To</label>
            <input type="date" value={to} onChange={e=>setTo(e.target.value)}
              style={{ border:`1.5px solid ${T.border}`, borderRadius:9, padding:'8px 11px',
                fontSize:12, fontFamily:FONTS.body, color:T.navy, outline:'none', width:155 }} />
          </div>
          {/* Preset chips */}
          <div style={{ display:'flex', gap:5, flexWrap:'wrap' }}>
            {PRESETS.map(p => {
              const active = from===p.from && to===p.to;
              return (
                <button key={p.label} onClick={()=>{ setFrom(p.from); setTo(p.to); }} style={{
                  padding:'7px 12px', borderRadius:8,
                  border:`1px solid ${active?T.teal:T.border}`,
                  background:active?`${T.teal}10`:T.surface,
                  color:active?T.teal:T.slate,
                  fontSize:11, fontWeight:700, cursor:'pointer', fontFamily:FONTS.body,
                }}>{p.label}</button>
              );
            })}
          </div>
          <button onClick={runReport} disabled={loading} style={{
            display:'flex', alignItems:'center', gap:7, padding:'10px 20px',
            borderRadius:10, border:'none',
            background:loading?T.border:`linear-gradient(135deg,${T.teal},${T.tealLight})`,
            color:'#fff', fontSize:13, fontWeight:700, cursor:loading?'not-allowed':'pointer',
            fontFamily:FONTS.body, boxShadow:loading?'none':`0 4px 14px ${T.teal}40`,
            transition:'all .2s',
          }}>
            {loading ? '⏳ Generating…' : '📊 Generate Report'}
          </button>
        </div>
        {/* Active filter pill */}
        {data && !loading && (
          <div style={{ marginTop:10, display:'flex', alignItems:'center', gap:8 }}>
            <span style={{ fontSize:10, background:`${T.teal}10`, color:T.teal, padding:'3px 10px',
              borderRadius:99, fontWeight:700 }}>
              {activeTab?.icon} {activeTab?.label} · {fmtD(from)} — {fmtD(to)}
            </span>
          </div>
        )}
      </div>

      {/* ── Offline / partial warning ── */}
      {(error || data?._offline) && data && (
        <div style={{ padding:'10px 16px', background:'#FEF9C3', border:'1px solid #FDE68A',
          borderRadius:10, marginBottom:12, fontSize:12, color:'#78350F', fontWeight:600,
          display:'flex', alignItems:'center', gap:8 }}>
          📱 {error || 'Showing cached data from your last online session — results may not include recent changes.'}
        </div>
      )}

      {/* ── Report output ── */}
      <div>
        {renderReport()}
      </div>
    </div>
  );
}
